import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import Button from 'Components/UI/Button';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Checkbox from '@material-ui/core/Checkbox';
import Typography from '@material-ui/core/Typography';
import { lighten } from '@material-ui/core/styles/colorManipulator';
import MuiAddCircleIcon from '@material-ui/icons/AddCircle';
import { withStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';
import BookmarkIcon from '@material-ui/icons/Bookmark';
import { Tooltip } from '@material-ui/core';

const styles = theme => ({
  root: {},
  listHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: theme.spacing(2),
  },
  listHeaderLeft: {
    display: 'flex',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
  },
  helperText: {
    textAlign: 'center',
    whiteSpace: 'pre-wrap',
    fontWeight: theme.typography.fontWeightBold,
  },
  list: {},
  listItem: {
    justifyContent: 'space-between',
  },
  listItemTextRoot: {
    flex: '0.7',
  },
  listItemText: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.body1.fontSize,
  },
  listItemSelected: {
    backgroundColor: lighten(theme.palette.secondary.main, 0.8),
    color: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: lighten(theme.palette.secondary.main, 0.8),
    },
  },
  addActionItemButton: {},
  addActionItemButtonIcon: {
    paddingRight: theme.spacing(1),
  },
  loadingSpinner: {
    padding: theme.spacing(2),
  },
  paraplannerLegend: {
    color: theme.palette.facet.accentBlue,
  },
  paraplannerIcon: {
    color: theme.palette.facet.accentBlue,
    alignSelf: 'center',
  },
  paraplannerIconRoot: {
    minWidth: '0',
  },
  secondaryAction: {
    right: 0,
  },
});

function ActionItemSelector({
  actionItems,
  selectedActionItemIds,
  addButtonText,
  className,
  classes,
  onAdd,
  paraplannerSelect,
  onClick,
  onToggle,
  title,
  loading,
  showBookmarkLegend,
}) {
  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.listHeader}>
        <div className={classes.listHeaderLeft}>
          <Typography className={classes.title} color="primary" variant="h5">
            {title}
          </Typography>
          {showBookmarkLegend && (
            <Tooltip title="This icon indicates tasks that are assigned to you.">
              <BookmarkIcon className={classes.paraplannerLegend} />
            </Tooltip>
          )}
        </div>
        {actionItems.length > 0 && (
          <Typography className={classes.helperText} color="secondary">
            {'Add to\nEmail'}
          </Typography>
        )}
      </div>
      {loading ? (
        <div className={classes.loadingSpinner}>
          <CircularProgress color="secondary" size={25} />
        </div>
      ) : (
        <List className={classes.listItem} dense>
          {actionItems.map((actionItem) => {
            const labelId = `action-item.${actionItem.id()}`;
            const isSelected = selectedActionItemIds.includes(actionItem.id());

            return (
              <ListItem
                button
                test="action-item-li-selector"
                className={classNames(classes.listItem, {
                  [classes.listItemSelected]: isSelected,
                })}
                dense
                key={actionItem.id()}
                onClick={() => onClick(actionItem)}
              >
                <ListItemText
                  classes={{ root: classes.listItemTextRoot, primary: classes.listItemText }}
                  id={labelId}
                  primary={actionItem.attr('name')}
                />
                {actionItem.attr('assignedTo') === 'me' && (
                  <ListItemIcon classes={{ root: classes.paraplannerIconRoot }}>
                    <BookmarkIcon className={classes.paraplannerIcon} />
                  </ListItemIcon>
                )}
                <ListItemSecondaryAction classes={{ root: classes.secondaryAction }}>
                  <ListItemIcon>
                    <Checkbox
                      checked={isSelected}
                      color="secondary"
                      tabIndex={-1}
                      disableRipple
                      inputProps={{ 'aria-labelledby': labelId }}
                      onClick={(event) => {
                        onToggle(actionItem);
                        event.stopPropagation();
                      }}
                    />
                  </ListItemIcon>
                </ListItemSecondaryAction>
              </ListItem>
            );
          })}
        </List>
      )}
      <Button
        color="secondary"
        onClick={onAdd}
        className={classes.addActionItemButton}
      >
        <MuiAddCircleIcon className={classes.addActionItemButtonIcon} />
        {addButtonText}
      </Button>
      {paraplannerSelect}
    </div>
  );
}

ActionItemSelector.defaultProps = {
  className: '',
  loading: false,
  paraplannerSelect: null,
  showBookmarkLegend: false,
};

ActionItemSelector.propTypes = {
  actionItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  addButtonText: PropTypes.string.isRequired,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  onAdd: PropTypes.func.isRequired,
  paraplannerSelect: PropTypes.element,
  onClick: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedActionItemIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  title: PropTypes.string.isRequired,
  loading: PropTypes.bool,
  showBookmarkLegend: PropTypes.bool,
};

export default withStyles(styles)(ActionItemSelector);
